import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import SignatureCanvas from "react-signature-canvas";
import { FaCheck, FaTrashAlt } from "react-icons/fa";

import "./SignatureCapture.css";

type SignatureMode = "type" | "draw";

interface FontStyleOption {
  fontFamily: string;
  label: string;
}

export const signatureFontStyles: FontStyleOption[] = [
  { fontFamily: "'Dancing Script'", label: "Clássica" },
  { fontFamily: "'Pacifico'", label: "Elegante" },
  { fontFamily: "'Great Vibes'", label: "Formal" },
];

export interface SignatureCaptureHandle {
  /** PNG em dataURL, ou null quando ainda não há assinatura válida. */
  toDataURL: () => string | null;
  clear: () => void;
}

interface SignatureCaptureProps {
  /** Nome usado para desenhar a assinatura no modo "estilo pronto". */
  name: string;
  disabled?: boolean;
  /** Disparado sempre que o conteúdo muda, informando se há assinatura utilizável. */
  onChange?: (hasSignature: boolean) => void;
}

/**
 * Captura de assinatura por estilo pronto ou desenho livre.
 * Extraído de SignaturePage para ser reutilizado na tela de Configurações
 * (assinatura automática), mantendo o mesmo resultado em PNG.
 */
export const SignatureCapture = forwardRef<
  SignatureCaptureHandle,
  SignatureCaptureProps
>(function SignatureCapture({ name, disabled = false, onChange }, ref) {
  const [signatureMode, setSignatureMode] = useState<SignatureMode>("type");
  const [selectedStyle, setSelectedStyle] = useState<FontStyleOption | null>(
    signatureFontStyles[0],
  );
  const [hasDrawnSignature, setHasDrawnSignature] = useState(false);

  const sigCanvas = useRef<any>(null);
  const canvasWrapperRef = useRef<HTMLDivElement>(null);

  const applyStyleToCanvas = (style: FontStyleOption) => {
    if (!sigCanvas.current) return;
    const canvas = sigCanvas.current.getCanvas?.();
    if (!canvas) return;

    const wrapperWidth = canvasWrapperRef.current?.clientWidth || 500;
    canvas.width = wrapperWidth;
    canvas.height = 200;

    const context = canvas.getContext("2d");
    context.clearRect(0, 0, canvas.width, canvas.height);
    context.fillStyle = "#111827";
    context.textAlign = "center";
    context.textBaseline = "middle";

    let fontSize = 64;
    context.font = `${fontSize}px ${style.fontFamily}`;

    const words = (name || "Sua Assinatura").split(" ");
    const lines: string[] = [];
    let currentLine = "";

    for (const word of words) {
      const testLine = currentLine + word + " ";
      const testWidth = context.measureText(testLine).width;
      if (testWidth > canvas.width - 60 && currentLine.length > 0) {
        lines.push(currentLine);
        currentLine = word + " ";
      } else {
        currentLine = testLine;
      }
    }
    lines.push(currentLine);

    while (lines.length * fontSize > canvas.height - 30) {
      fontSize -= 2;
      context.font = `${fontSize}px ${style.fontFamily}`;
    }

    let y = (canvas.height - lines.length * fontSize) / 2 + fontSize / 2;
    for (const line of lines) {
      context.fillText(line.trim(), canvas.width / 2, y);
      y += fontSize;
    }
  };

  useEffect(() => {
    if (signatureMode !== "type") return;
    if (!selectedStyle) return;
    applyStyleToCanvas(selectedStyle);
    onChange?.(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signatureMode, selectedStyle, name]);

  useEffect(() => {
    const onResize = () => {
      if (signatureMode === "type" && selectedStyle) {
        applyStyleToCanvas(selectedStyle);
      } else if (sigCanvas.current && canvasWrapperRef.current) {
        const canvas = sigCanvas.current.getCanvas?.();
        if (canvas) {
          canvas.width = canvasWrapperRef.current.clientWidth;
          canvas.height = 200;
        }
      }
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signatureMode, selectedStyle]);

  const clearDrawing = () => {
    sigCanvas.current?.clear?.();
    setHasDrawnSignature(false);
    onChange?.(false);
  };

  const switchToDraw = () => {
    setSignatureMode("draw");
    setSelectedStyle(null);
    onChange?.(false);
    setTimeout(() => {
      sigCanvas.current?.clear?.();
      const canvas = sigCanvas.current?.getCanvas?.();
      if (canvas && canvasWrapperRef.current) {
        canvas.width = canvasWrapperRef.current.clientWidth;
        canvas.height = 200;
      }
      setHasDrawnSignature(false);
    }, 50);
  };

  const switchToType = (style: FontStyleOption) => {
    setSignatureMode("type");
    setSelectedStyle(style);
    setHasDrawnSignature(false);
  };

  useImperativeHandle(ref, () => ({
    toDataURL: () => {
      if (signatureMode === "type" && selectedStyle) {
        const canvas = sigCanvas.current?.getCanvas?.();
        return canvas ? canvas.toDataURL("image/png") : null;
      }

      if (signatureMode === "draw" && hasDrawnSignature && sigCanvas.current) {
        try {
          return sigCanvas.current.getTrimmedCanvas().toDataURL("image/png");
        } catch {
          return sigCanvas.current.toDataURL("image/png");
        }
      }

      return null;
    },
    clear: clearDrawing,
  }));

  return (
    <div className="sigcapture">
      <div className="sigcapture__tabs">
        <button
          type="button"
          className={`sigcapture__tab ${
            signatureMode === "type" ? "sigcapture__tab--active" : ""
          }`}
          onClick={() => switchToType(selectedStyle ?? signatureFontStyles[0])}
          disabled={disabled}
        >
          Estilo pronto
        </button>
        <button
          type="button"
          className={`sigcapture__tab ${
            signatureMode === "draw" ? "sigcapture__tab--active" : ""
          }`}
          onClick={switchToDraw}
          disabled={disabled}
        >
          Desenhar
        </button>
      </div>

      {signatureMode === "type" && (
        <div className="sigcapture__styles">
          {signatureFontStyles.map((style) => {
            const isActive = selectedStyle?.fontFamily === style.fontFamily;
            return (
              <button
                type="button"
                key={style.fontFamily}
                onClick={() => switchToType(style)}
                className={`sigcapture__style ${
                  isActive ? "sigcapture__style--active" : ""
                }`}
                disabled={disabled}
              >
                {isActive && (
                  <span className="sigcapture__style-check">
                    <FaCheck />
                  </span>
                )}
                <span
                  className="sigcapture__style-preview"
                  style={{ fontFamily: style.fontFamily }}
                >
                  {name?.split(" ")[0] || "Assinatura"}
                </span>
                <span className="sigcapture__style-label">{style.label}</span>
              </button>
            );
          })}
        </div>
      )}

      <div className="sigcapture__canvas-block">
        <div className="sigcapture__canvas-header">
          <span className="sigcapture__canvas-title">Pré-visualização</span>
          {signatureMode === "draw" && (
            <button
              type="button"
              onClick={clearDrawing}
              className="sigcapture__clear-btn"
              disabled={disabled}
            >
              <FaTrashAlt size={10} /> Limpar
            </button>
          )}
        </div>
        <div className="sigcapture__canvas-wrapper" ref={canvasWrapperRef}>
          <SignatureCanvas
            // @ts-ignore
            ref={sigCanvas}
            penColor="#111827"
            onEnd={() => {
              setHasDrawnSignature(true);
              onChange?.(true);
            }}
            canvasProps={{ className: "sigcapture__canvas" }}
          />
          {signatureMode === "draw" && !hasDrawnSignature && (
            <span className="sigcapture__canvas-hint">
              Desenhe aqui com o dedo ou mouse
            </span>
          )}
        </div>
      </div>
    </div>
  );
});
